import type React from "react";
import {memo, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import type {NativeSyntheticEvent, TextInputSelectionChangeEventData} from "react-native";

import type {LanguageCode} from "../../constant/languages";
import {useView} from "../../hooks/useView";
import EyeIcon from "../../react/components/Icons/EyeIcon";
import HTTPContext from "../../react/contexts/HTTPContext";
import usePrevious from "../../react/hooks/usePrevious";
import MessageRuleService from "../../services/messageRule";
import Label from "../Label";
import Loader from "../Loader";
import {Modal} from "../Modal";
import PreText from "../PreText";
import type {SelectLanguageOption} from "../SelectLanguage";
import SelectLanguage from "../SelectLanguage";
import SelectMessageTags from "../SelectMessageTags";
import type {SelectTagEvent, TagOption} from "../SelectMessageTags/types";
import {TabGroup, TabItem} from "../Tabs";
import {Alert} from "../ui/alert";
import {Box} from "../ui/box";
import {Button, ButtonIcon, ButtonText} from "../ui/button";
import {FormControlLabelText} from "../ui/formcontrol";
import {HStack} from "../ui/hstack";
import {Textarea, TextareaInput} from "../ui/textarea";
import {VStack} from "../ui/vstack";

type ViewMode = "default" | "preview";

type Props = {
    messages: {[key in LanguageCode]?: string};
    event: SelectTagEvent;
    error?: string;
    label?: string;
    tooltip?: string;
    tagsEnabled?: boolean;
    testID?: string;
    languagesEnabled?: boolean;
    disabled?: boolean;
    onChange: (value: string, language: LanguageCode) => void;
};

const TextareaWithTags: React.FC<Props> = ({
    messages,
    event: initEvent,
    error: initError = "",
    label = "message",
    tooltip = "",
    languagesEnabled = false,
    tagsEnabled = true,
    testID = "input-message",
    disabled = false,
    onChange
}) => {
    const http = useContext(HTTPContext);
    const textArea = useRef<React.ElementRef<typeof TextareaInput>>(null);
    const view = useView();
    const isMobile = view === "native" || view === "webMobile";

    const [value, setValue] = useState<string>(messages.default || "");
    const [event, setEvent] = useState<SelectTagEvent>(initEvent);
    const [error, setError] = useState(initError);
    const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>("default");
    const [viewMode, setViewMode] = useState<ViewMode>("default");
    const [previewMessage, setPreviewMessage] = useState<string>();
    const [cursorPosition, setCursorPosition] = useState<number>();
    const previousMessages = usePrevious(messages);

    const handleCursorChange = useCallback(
        (event: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
            const {selection} = event.nativeEvent;
            setCursorPosition(selection.start);
        },
        []
    );

    const handleTag = useCallback(
        (tag: TagOption) => {
            let newValue = value;
            let newCursorPosition = cursorPosition;
            if (newCursorPosition === undefined) {
                newCursorPosition = value.length;
            }
            const textBeforeCursorPosition = newValue.substring(0, cursorPosition);
            const textAfterCursorPosition = newValue.substring(newCursorPosition, value.length);
            newValue = textBeforeCursorPosition + tag.value + textAfterCursorPosition;
            newCursorPosition += tag.value.length;
            setValue(newValue);
            setCursorPosition(newCursorPosition);
            if (textArea.current) {
                const textAreaEl = textArea.current;
                if (process.env.IS_WEB && textAreaEl instanceof HTMLTextAreaElement) {
                    textAreaEl.focus();
                    requestAnimationFrame(() => {
                        textAreaEl.setSelectionRange(newCursorPosition, newCursorPosition);
                    });
                }

                if ("focus" in textAreaEl && typeof textAreaEl.focus === "function") {
                    textAreaEl.focus();
                    textAreaEl.selection = {
                        start: newCursorPosition,
                        end: newCursorPosition
                    };
                }
            }
            onChange(newValue, currentLanguage);
        },
        [currentLanguage, cursorPosition, onChange, value]
    );

    const handleLanguageChange = useCallback(
        (option: SelectLanguageOption) => {
            setCurrentLanguage(option.value);
            setValue(messages[option.value] || "");
        },
        [messages]
    );

    const handlePreview = useCallback(async () => {
        if (!value) {
            return;
        }

        setViewMode("preview");
        setPreviewMessage("");

        const messageRuleService = new MessageRuleService(http);
        const message = await messageRuleService.previewMessage(currentLanguage, value);

        setPreviewMessage(message);
    }, [currentLanguage, http, value]);

    const handleClickLabel = useCallback(() => {
        setViewMode("default");
    }, []);

    const handleChangeViewMode = useCallback(
        async (viewMode: ViewMode) => {
            setViewMode(viewMode);
            if (viewMode === "preview") {
                setViewMode("preview");
                setPreviewMessage("");

                const messageRuleService = new MessageRuleService(http);
                const message = await messageRuleService.previewMessage(currentLanguage, value);

                setPreviewMessage(message);
            }
        },
        [currentLanguage, http, value]
    );

    const handleChangeText = useCallback(
        (value: string) => {
            setValue(value);
            onChange(value, currentLanguage);
        },
        [currentLanguage, onChange]
    );

    useEffect(() => {
        setEvent(initEvent);
        setError(initError);
        setViewMode("default");
        setPreviewMessage("");
    }, [initEvent, initError, currentLanguage]);

    useEffect(() => {
        if (JSON.stringify(messages) !== JSON.stringify(previousMessages)) {
            setViewMode("default");
            setPreviewMessage("");
            const languages = Object.keys(messages);
            if (languages.length > 0 && !languages.includes(currentLanguage)) {
                setCurrentLanguage(languages[0]);
                setValue(messages[languages[0]] || "");
            } else {
                setValue(messages[currentLanguage] || "");
            }
        }
    }, [messages, currentLanguage, previousMessages]);

    const supportedLanguages = useMemo(() => Object.keys(messages), [messages]);

    return (
        <VStack space="lg">
            {isMobile && (
                <TabGroup activeTab={viewMode} onTabChange={handleChangeViewMode}>
                    <TabItem className="flex-1" id="default">
                        Customize
                    </TabItem>
                    <TabItem className="flex-1" disabled={!value} id="preview">
                        Preview
                    </TabItem>
                </TabGroup>
            )}
            <VStack className="justify-start" space="xs">
                {(viewMode === "default" || !isMobile) && (
                    <HStack className="items-center justify-between">
                        <Label size="xs" tooltip={tooltip}>
                            <FormControlLabelText color="blueGray.400" variant="xs">
                                Write {label}
                            </FormControlLabelText>
                        </Label>
                        {!isMobile && (
                            <Button
                                disabled={!value}
                                size="sm"
                                variant="ghost"
                                onPress={handlePreview}
                            >
                                <ButtonIcon as={EyeIcon} />
                                <ButtonText>Preview Message</ButtonText>
                            </Button>
                        )}
                    </HStack>
                )}
                {languagesEnabled && (
                    <SelectLanguage
                        languages={supportedLanguages}
                        onSelectedOption={handleLanguageChange}
                    />
                )}
                {(viewMode === "default" || !isMobile) && (
                    <VStack className="relative">
                        <Textarea className="h-40" isDisabled={disabled} isInvalid={!!error}>
                            <TextareaInput
                                ref={textArea}
                                id="message"
                                placeholder=""
                                testID={testID}
                                value={value}
                                onChangeText={handleChangeText}
                                onSelectionChange={handleCursorChange}
                            />
                            {tagsEnabled && (
                                <Box className="w-full px-3 py-2 h-10 border-t border-solid border-blueGray-200 items-start" />
                            )}
                        </Textarea>
                        {tagsEnabled && !disabled ? (
                            <Box className="absolute bottom-2 left-3">
                                <SelectMessageTags event={event} onSelectedOption={handleTag} />
                            </Box>
                        ) : undefined}
                    </VStack>
                )}

                {viewMode === "preview" &&
                    (isMobile ? (
                        <Box className="border border-solid border-blueGray-200 rounded-lg min-h-[80px] p-4">
                            {previewMessage ? (
                                <PreText color="blueGray.800" variant="sm">
                                    {previewMessage}
                                </PreText>
                            ) : (
                                <Loader center className="h-40" />
                            )}
                        </Box>
                    ) : (
                        <Modal
                            isOpen
                            btnTextCancel="Close"
                            size="md"
                            title="Preview Message"
                            onClose={handleClickLabel}
                        >
                            <Box className="border border-solid border-blueGray-200 rounded-lg min-h-[80px] p-4">
                                {previewMessage ? (
                                    <PreText color="blueGray.800" variant="sm">
                                        {previewMessage}
                                    </PreText>
                                ) : (
                                    <Loader center className="h-40" />
                                )}
                            </Box>
                        </Modal>
                    ))}

                {error && (
                    <Alert action="error" variant="subtle">
                        {error}
                    </Alert>
                )}
            </VStack>
        </VStack>
    );
};

export default memo(TextareaWithTags);
